// LearnCore Frontend - 图片显示组件

import React, { useState } from 'react';
import { PhotoIcon, EyeIcon } from '@heroicons/react/24/outline';

interface ImageDisplayProps {
  imageUrl: string;
  alt?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  allowPreview?: boolean;
}

export const ImageDisplay: React.FC<ImageDisplayProps> = ({ 
  imageUrl, 
  alt = '图片',
  className = '',
  size = 'md',
  allowPreview = true
}) => {
  const [imageError, setImageError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // 处理图片加载错误
  const handleImageError = () => {
    setImageError(true);
  };

  // 如果是markdown格式的图片链接，解析出真实URL和alt文本
  const parseImageUrl = (url: string): { realUrl: string; altText: string } => {
    // 匹配 ![alt](url) 格式
    const markdownMatch = url.match(/!\[(.*?)\]\((.*?)\)/);
    if (markdownMatch) {
      return {
        realUrl: markdownMatch[2],
        altText: markdownMatch[1] || alt
      };
    }
    return {
      realUrl: url,
      altText: alt
    };
  };

  const { realUrl, altText } = parseImageUrl(imageUrl);

  // 尺寸样式
  const sizeClasses = {
    sm: 'h-16 w-auto max-w-24',
    md: 'h-24 w-auto max-w-32',
    lg: 'h-32 w-auto max-w-48',
    xl: 'h-48 w-auto max-w-72',
    '2xl': 'h-64 w-auto max-w-96'
  } as const;

  // 如果图片加载失败，显示占位符
  if (imageError) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded border border-gray-200 ${sizeClasses[size]} ${className}`}>
        <div className="text-center text-gray-500">
          <PhotoIcon className="h-6 w-6 mx-auto mb-1" />
          <div className="text-xs">图片加载失败</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`relative inline-block ${className}`} onClick={(e) => e.stopPropagation()}>
        <img
          src={realUrl}
          alt={altText}
          className={`object-contain rounded border border-gray-200 ${sizeClasses[size]}`}
          onError={handleImageError}
        />
        {allowPreview && (
          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity rounded flex items-center justify-center opacity-0 hover:opacity-100">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
              className="p-1 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow"
            >
              <EyeIcon className="h-3 w-3 text-gray-600" />
            </button>
          </div>
        )}
      </div>

      {/* 图片预览模态窗口 */}
      {previewOpen && allowPreview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" 
          onClick={() => setPreviewOpen(false)}
        >
          <div className="max-w-4xl max-h-[90vh] p-4">
            <img
              src={realUrl}
              alt={altText}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <button
            onClick={() => setPreviewOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};

export default ImageDisplay;

